import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '../services/notificationService.ts';

interface UseNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isEnabled: boolean;
  hasPermission: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  setEnabled: (enabled: boolean) => void;
}

/**
 * Hook para gerenciar notificações no aplicativo
 */
export const useNotifications = (): UseNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission>(
    notificationService.getPermission()
  );
  const [isEnabled, setIsEnabled] = useState(
    notificationService.isEnabledByUser()
  );

  // Atualizar permissão quando mudar
  useEffect(() => {
    const checkPermission = () => {
      const currentPermission = notificationService.getPermission();
      if (currentPermission !== permission) {
        setPermission(currentPermission);
      }
    };

    // Verificar permissão periodicamente
    const interval = setInterval(checkPermission, 1000);
    return () => clearInterval(interval);
  }, [permission]);

  // Atualizar estado de habilitado quando mudar
  useEffect(() => {
    setIsEnabled(notificationService.isEnabledByUser());
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    try {
      const newPermission = await notificationService.requestPermission();
      setPermission(newPermission);
      return newPermission;
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      throw error;
    }
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    notificationService.setEnabled(enabled);
    setIsEnabled(enabled);
  }, []);

  return {
    isSupported: notificationService.isSupported(),
    permission,
    isEnabled,
    hasPermission: notificationService.hasPermission(),
    requestPermission,
    setEnabled,
  };
};

